import { useEffect, useState } from "react";
import type { Opportunity } from "@/types/opportunity";
import { updateOpportunityFulfillment } from "@/lib/opportunity/opportunityClient";
import {
  INSTANCE_FULFILLMENT_KINDS,
  opportunityFulfillmentIsLocked,
} from "@/lib/opportunity/instanceFulfillment";
import { OpportunityFollowOnDealField } from "@/components/opportunity/OpportunityFollowOnDealField";

export function OpportunityFulfillmentField(props: {
  opportunity: Opportunity;
  disabled?: boolean;
  onChanged: () => void;
  setMessage: (msg: { text: string; ok: boolean } | null) => void;
}) {
  const { opportunity, disabled, onChanged, setMessage } = props;
  const locked = opportunityFulfillmentIsLocked(opportunity.status);
  const [serialDraft, setSerialDraft] = useState(opportunity.mfg_unit_serial ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSerialDraft(opportunity.mfg_unit_serial ?? "");
  }, [opportunity.id, opportunity.mfg_unit_serial]);

  async function save(patch: { fulfillment?: "inventory" | "custom" | null; mfg_unit_serial?: string | null }) {
    setBusy(true);
    setMessage(null);
    const r = await updateOpportunityFulfillment({
      opportunity_id: opportunity.id,
      ...patch,
    });
    setBusy(false);
    if (!r.ok) {
      setMessage({ text: r.error, ok: false });
      return;
    }
    onChanged();
  }

  const fulfillment = opportunity.fulfillment ?? "";

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 py-1 min-w-0">
        <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400 w-24 shrink-0">
          Fulfillment
        </span>
        <select
          value={fulfillment}
          disabled={disabled || busy || locked}
          onChange={(e) => {
            const v = e.target.value;
            void save({ fulfillment: v === "inventory" || v === "custom" ? v : null });
          }}
          className="min-w-0 flex-1 max-w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
        >
          <option value="">Unset</option>
          {INSTANCE_FULFILLMENT_KINDS.map((k) => (
            <option key={k} value={k}>
              {k === "inventory" ? "Inventory (lot / off-lot)" : "Custom (new-build)"}
            </option>
          ))}
        </select>
      </div>
      {fulfillment === "inventory" || opportunity.mfg_unit_id ? (
        <div className="flex items-center gap-2 py-1 min-w-0">
          <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400 w-24 shrink-0">
            Lot serial
          </span>
          <input
            value={serialDraft}
            disabled={disabled || busy || locked}
            onChange={(e) => setSerialDraft(e.target.value)}
            onBlur={() => {
              const next = serialDraft.trim();
              const prev = (opportunity.mfg_unit_serial ?? "").trim();
              if (next === prev) return;
              void save({ mfg_unit_serial: next || null });
            }}
            placeholder="Attach a lot serial"
            className="min-w-0 flex-1 max-w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
          />
        </div>
      ) : null}
      <p className="text-xs text-gray-400 pl-[6.5rem]">
        Same opportunity — switch stock and custom here. Detach the serial to start custom.
        Does not replace the unit’s original sale pointer.
      </p>
      <OpportunityFollowOnDealField
        opportunity={opportunity}
        disabled={disabled || busy}
        setMessage={setMessage}
      />
    </div>
  );
}
